/**
 * Panel Width Utilities
 *
 * Manages the chat panel width with localStorage persistence.
 * Extracted from Chat.tsx for testability and reusability.
 */

import { logger } from "@/telemetry";

export const CHAT_PANEL_WIDTH_KEY = "chatPanelWidth";
export const DEFAULT_CHAT_WIDTH = 65;
export const MIN_CHAT_WIDTH = 20;
export const MAX_CHAT_WIDTH = 75;

/**
 * Get the initial chat panel width from localStorage.
 * Returns the default width if no valid value is saved.
 */
export function getInitialChatWidth(): number {
  try {
    const saved = localStorage.getItem(CHAT_PANEL_WIDTH_KEY);

    if (saved) {
      const width = parseFloat(saved);

      // Validate the saved width is within acceptable range
      if (width >= MIN_CHAT_WIDTH && width <= MAX_CHAT_WIDTH) {
        return width;
      }
    }
  } catch (error) {
    logger.warn("Failed to load saved panel width:", { error: String(error) });
  }

  return DEFAULT_CHAT_WIDTH;
}

/**
 * Save the chat panel width to localStorage.
 */
export function saveChatWidth(width: number): void {
  try {
    localStorage.setItem(CHAT_PANEL_WIDTH_KEY, width.toString());
  } catch (error) {
    logger.warn("Failed to save panel width:", { error: String(error) });
  }
}

/**
 * Constrain a width value to the valid range.
 */
export function constrainWidth(width: number): number {
  return Math.max(MIN_CHAT_WIDTH, Math.min(MAX_CHAT_WIDTH, width));
}
